'use client';

/**
 * Personal-workspace provisioning primitive, shared by the funnel's
 * setup screen and the first-run setup card.
 *
 * One implementation of: provision (or adopt the existing workspace on a
 * 409 replay), repair Clerk membership, activate the org. Screens keep
 * their own choreography (forms, polling, navigation); this hook owns the
 * API sequencing and the error mapping so both screens behave alike.
 */

import { useOrganizationList } from '@clerk/nextjs';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api-client';
import { useApi } from '@/lib/use-api';
import {
  provisionPersonalWorkspace,
  recoverWorkspace,
  type ProvisionResult,
} from '@/features/account/api';

export interface PersonalWorkspaceRequest {
  workspace_name: string;
  idempotency_key: string;
  turnstile_token?: string;
  challenge_stamp?: string;
  full_name?: string;
  company_domain?: string;
}

export type ProvisionOutcome =
  | { ok: true; result: ProvisionResult }
  | { ok: false; message: string };

function detailOf(err: unknown): string {
  if (!(err instanceof ApiError)) return '';
  const body = (err.body ?? {}) as { Detail?: unknown; detail?: unknown };
  const detail = body.Detail ?? body.detail;
  return typeof detail === 'string' ? detail.trim() : '';
}

export function mapProvisionError(err: unknown): string {
  if (err instanceof ApiError && err.status === 502) {
    const code = (err.body as { code?: string } | null | undefined)?.code;
    if (code === 'clerk_repair_failed' || code === 'clerk_identity') {
      return 'Your workspace already exists — retry opening it rather than creating a new one.';
    }
    return 'Our engine is waking up. Retry — your progress is saved.';
  }
  if (err instanceof ApiError && err.status === 403) {
    const detail = detailOf(err);
    // Prefer real API detail (auth/link errors) over the self-serve-disabled guess.
    if (detail && !/self-serve.*disabled/i.test(detail)) return detail;
    return 'Self-serve workspace creation is currently disabled. Please contact support.';
  }
  if (err instanceof ApiError && err.status === 400) {
    return detailOf(err) || 'Something looks wrong with that request. Please try again.';
  }
  return 'We could not set up your workspace. Please try again.';
}

export function usePersonalWorkspace() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { setActive } = useOrganizationList();

  /**
   * Provision the caller's personal workspace, adopting the existing one
   * on a 409 replay (response lost on a prior attempt). Repairs Clerk
   * membership first so activation succeeds even when Clerk-side
   * membership went missing.
   */
  async function provisionWorkspace(req: PersonalWorkspaceRequest): Promise<ProvisionOutcome> {
    try {
      const result = await provisionPersonalWorkspace(api, {
        workspace_name: req.workspace_name,
        idempotency_key: req.idempotency_key,
        accepted_terms: true,
        turnstile_token: req.turnstile_token,
        challenge_stamp: req.challenge_stamp,
        full_name: req.full_name,
        company_domain: req.company_domain,
      });
      return { ok: true, result };
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const body = (err.body ?? {}) as { result?: ProvisionResult };
        if (body.result?.clerk_organization_id) {
          let result = body.result;
          try {
            result = await recoverWorkspace(api);
          } catch {
            // Fall through with the 409 result; activation surfaces failures.
          }
          return { ok: true, result };
        }
        return { ok: false, message: 'You already have a personal workspace. Please refresh the page.' };
      }
      return { ok: false, message: mapProvisionError(err) };
    }
  }

  /**
   * Activate the org in Clerk and clear cached workspace context.
   * Returns false (never throws) so screens can offer a retry.
   */
  async function activateWorkspace(clerkOrganizationId: string): Promise<boolean> {
    if (!setActive) return false;
    try {
      await setActive({ organization: clerkOrganizationId });
      await queryClient.clear();
      return true;
    } catch {
      return false;
    }
  }

  return { provisionWorkspace, activateWorkspace };
}
